import { TFunction } from 'next-i18next'

import { DEFAULT_COUNTRY, SUPPORTED_COUNTRIES } from '@src/config/localisation/locale-config.constants'
import {
  addressStateMetaServiceClient,
  addressValidationClient,
  customerServicesV2Client,
} from '@src/quince-site-common/src/utils/HttpClient'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { FEATURES, MODULES } from '../../constants/template-info'
import QuinceApiError from '../../domains/quince-api-error'

import { addressGoogleAutocompleteHolder } from './address-google-autocomplete.holder'
import { getErrorStates } from './address-validation.utils'
import { Address, InvalidAddressReasonType } from './addressType'

export const INVALID_ZIP_ERROR_CODES = {
  INVALID_ZIP_PROVINCE_FORMAT: 'INVALID_ZIP_PROVINCE_FORMAT',
  UNSERVICEABLE_ZIP: 'UNSERVICEABLE_ZIP',
} as const

export interface ValidateAddressOptions {
  sessionId?: string | null
  isSuggestedAddress?: boolean
}

/** Clears Google autocomplete tokens and validate metadata (e.g. when closing address modal). */
export const resetAddressValidationSession = () => {
  addressGoogleAutocompleteHolder.reset()
}

interface PostalCodeValidationResponse {
  success: boolean
  message?: string
  state?: string
  errorCode?: string | null
}

interface ZipCodeStateValidationResponse {
  success: boolean
  message?: string
  errorCode?: string
}

export const createAddress = async (
  address: Address,
  isDefault?: boolean,
  enrichedAddress?: Partial<Address> | null,
) => {
  try {
    // CS V2: addresses live under /customer-services/customers/addresses. Returns 201 Created
    // (axios resolves all 2xx, so no status-code branching is needed here).
    const { data } = await customerServicesV2Client.post<Address>(`/addresses`, {
      ...address,
      ...(enrichedAddress ?? {}),
      isDefault,
    })
    return {
      ...data,
      addressId: data.id,
    }
  } catch (e) {
    const quinceError = new QuinceApiError({
      error: e as Error,
      // @i18n-ignore
      message: 'Failed while creating address in adress service createAddress() runtime',
      apiName: 'CREATE_ADDRESS',
    })
    captureException(quinceError, {
      module: MODULES.ADDRESS,
      feature: FEATURES.CREATE_ADDRESS,
      extraInfo: {
        capturedFrom: 'createAddress',
      },
    })
    console.error('Customer does not have permission to create the address')
  }
}

export const editAddress = async (
  address: Address,
  oldAddressId: string,
  customerId: number,
  isDefault: boolean,
  enrichedAddress?: Partial<Address> | null,
) => {
  try {
    const { data } = await customerServicesV2Client.put<Address>(`/addresses/${oldAddressId}`, {
      ...enrichedAddress,
      ...address,
      customerId,
      isDefault,
    })
    return {
      ...address,
      addressId: data.id,
    }
  } catch (e) {
    console.error('Customer does not have permission to edit the address')
    const quinceError = new QuinceApiError({
      error: e as Error,
      // @i18n-ignore
      message: 'Failed while creating address in adress service editAddress() runtime',
      apiName: 'EDIT_ADDRESS',
    })
    captureException(quinceError, {
      module: MODULES.ADDRESS,
      feature: FEATURES.EDIT_ADDRESS,
      extraInfo: {
        capturedFrom: 'editAddress',
      },
    })
    return {}
  }
}

export const deleteAddress = async (addressId: string) => {
  try {
    const { data } = await customerServicesV2Client.delete<Address[]>(`/addresses/${addressId}`)
    return data
  } catch (e) {
    const quinceError = new QuinceApiError({
      error: e as Error,
      // @i18n-ignore
      message: 'Failed while deleting address in adress service deleteAddress() runtime',
      apiName: 'DELETE_ADDRESS',
    })
    captureException(quinceError, {
      module: MODULES.ADDRESS,
      feature: FEATURES.DELETE_ADDRESS,
      extraInfo: {
        capturedFrom: 'deleteAddress',
      },
    })
    console.error('Customer does not have permission to delete the address')
    return {}
  }
}

export const getAllSavedAddress = async () => {
  try {
    const { data } = await customerServicesV2Client.get<Address[]>(`/addresses`)
    return data
  } catch (e) {
    console.error('Unable to fetch all saved address')
    const quinceError = new QuinceApiError({
      error: e as Error,
      // @i18n-ignore
      message: 'Failed while fetching address in adress service getAllSavedAddress() runtime',
      apiName: 'FETCH_ADDRESS',
    })
    captureException(quinceError, {
      module: MODULES.ADDRESS,
      feature: FEATURES.FETCH_ADDRESS,
      extraInfo: {
        capturedFrom: 'getAllSavedAddress',
      },
    })
    return []
  }
}

export const getAllStatesOption = async (countryCode = DEFAULT_COUNTRY.toUpperCase()) => {
  try {
    const safeCountry = countryCode && countryCode !== 'undefined' ? countryCode : DEFAULT_COUNTRY.toUpperCase()
    const isCountrySupported = SUPPORTED_COUNTRIES.some(
      supportedCountry => supportedCountry === safeCountry.toLowerCase(),
    )
    const validatedCountry = isCountrySupported ? safeCountry : DEFAULT_COUNTRY.toUpperCase()
    const { data } = await addressStateMetaServiceClient.get(
      `/address-service/v1/metadata/states?countryCode=${validatedCountry}`,
    )
    return data
  } catch (e) {
    const quinceError = new QuinceApiError({
      error: e as Error,
      // @i18n-ignore
      message: 'Failed while fetching address in adress service getAllStatesOption() runtime',
      apiName: 'FETCH_STATE_OPTIONS',
    })
    captureException(quinceError, {
      module: MODULES.ADDRESS,
      feature: FEATURES.FETCH_ADDRESS,
      extraInfo: {
        capturedFrom: 'getAllSavedAddress',
      },
    })
    console.error('Unable to fetch all saved address')
    return {
      states: [],
    }
  }
}

export const validatePostalCode = async (postalCode: string, countryCode: string, t?: TFunction) => {
  try {
    const { data } = await addressStateMetaServiceClient.get<PostalCodeValidationResponse>(
      `/address-service/v1/validate/postal-code?countryCode=${countryCode}&postalCode=${postalCode}`,
    )
    return {
      success: !!data.success,
      message:
        data.errorCode === INVALID_ZIP_ERROR_CODES.UNSERVICEABLE_ZIP && t ? t('do_not_ship_error') : data.message,
      state: data.state,
      errorCode: data.errorCode ?? null,
    }
  } catch (e) {
    return {
      success: false,
    }
  }
}

export const validateZipCodeStateCombination = async (
  zipCode: string,
  provinceCode: string,
  country = DEFAULT_COUNTRY.toUpperCase(),
  t?: TFunction,
) => {
  try {
    const isCountrySupported = SUPPORTED_COUNTRIES.some(supportedCountry => supportedCountry === country.toLowerCase())
    const validatedCountry = isCountrySupported ? country : DEFAULT_COUNTRY.toUpperCase()
    const { data } = await addressStateMetaServiceClient.get<ZipCodeStateValidationResponse>(
      `/address-service/v1/validate/state?zipCode=${zipCode}&state=${provinceCode}&countryCode=${validatedCountry.toUpperCase()}`,
    )
    const errorCode = data.errorCode as
      | (typeof INVALID_ZIP_ERROR_CODES)[keyof typeof INVALID_ZIP_ERROR_CODES]
      | undefined
    return {
      success: data.success,
      message: errorCode === INVALID_ZIP_ERROR_CODES.UNSERVICEABLE_ZIP && t ? t('do_not_ship_error') : data.message,
      errorCode,
    }
  } catch (e) {
    return {
      success: false,
    }
  }
}

export const validateAddress = async (address: Address, t: TFunction, options?: ValidateAddressOptions) => {
  try {
    const requestBody: Address & {
      sessionToken?: string
      requestId?: string
      sessionId?: string | null
      metadata?: Record<string, string> | null
    } = {
      ...address,
      sessionToken: addressGoogleAutocompleteHolder.getGoogleAutoCompleteSessionId(),
      requestId: addressGoogleAutocompleteHolder.getGoogleAutoCompleteRequestId(),
      sessionId: options?.sessionId ?? undefined,
      metadata: options?.isSuggestedAddress
        ? (addressGoogleAutocompleteHolder.getPrevAddressMetadata() ?? undefined)
        : undefined,
    }
    const { data } = await addressValidationClient.post<
      typeof requestBody,
      {
        invalidAddressReasons?: InvalidAddressReasonType[]
        isAddressValid: boolean
        responseId?: string
        addressMetadata?: Record<string, string>
        enrichedAddress?: Address | null
      }
    >(`/check/validate`, requestBody, {
      params: {
        isSuggestedAddress: options?.isSuggestedAddress ? true : undefined,
      },
    })
    if (data.responseId) {
      addressGoogleAutocompleteHolder.setGoogleAutoCompleteRequestId(data.responseId)
    }
    addressGoogleAutocompleteHolder.setPrevAddressMetadata(data.addressMetadata ?? null)
    return {
      ...getErrorStates(data.invalidAddressReasons ?? [], t),
      isAddressValid: data.isAddressValid,
      enrichedAddress: data.enrichedAddress ?? null,
    }
  } catch (e) {
    addressGoogleAutocompleteHolder.setPrevAddressMetadata(null)
    console.error('Customer does not have permission to create the address')
    const quinceError = new QuinceApiError({
      error: e as Error,
      // @i18n-ignore
      message: 'Failed while fetching address in adress service validateAddress() runtime',
      apiName: 'VALIDATE_ADDRESS',
    })
    captureException(quinceError, {
      module: MODULES.ADDRESS,
      feature: FEATURES.VALIDATE_ADDRESS,
      extraInfo: {
        capturedFrom: 'validateAddress',
      },
    })
    return {}
  }
}
